/**
 * ReportContentModal — universal "Report to Moderator" flow.
 * Works for forum topics, replies, and DMs (pass the right target prop).
 *
 * Props:
 *   open, onClose
 *   target: { topic_id? | reply_id? | dm_message_id?, snippet? }
 *   API
 */
import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Flag, X, ShieldAlert } from 'lucide-react';

// Facebook-style comprehensive reporting categories. Each reason has a
// severity band the backend can use to fast-track high-severity cases
// (soft-lock the target until a moderator reviews).
const REASONS = [
  { value: 'sexual',             label: 'Nudity or sexually explicit content', severity: 'high' },
  { value: 'violence_threats',   label: 'Violence or credible threats',        severity: 'high' },
  { value: 'self_harm',          label: 'Self-harm or suicide',                severity: 'high' },
  { value: 'hate',               label: 'Hate speech or racism',               severity: 'high' },
  { value: 'graphic_content',    label: 'Graphic or disturbing content',       severity: 'high' },
  { value: 'harassment',         label: 'Harassment or bullying',              severity: 'medium' },
  { value: 'impersonation',      label: 'Impersonation or fake account',       severity: 'medium' },
  { value: 'misinformation',     label: 'Dangerous misinformation',            severity: 'medium' },
  { value: 'spam',               label: 'Spam or self-promotion',              severity: 'low' },
  { value: 'contact_solicit',    label: 'Sharing personal contact info',       severity: 'low' },
  { value: 'other',              label: 'Something else',                      severity: 'low' },
];

const ReportContentModal = ({ open, onClose, target }) => {
  const { API } = useContext(AuthContext);
  const [reason, setReason] = useState('spam');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/forum/reports`, {
        topic_id:      target?.topic_id,
        reply_id:      target?.reply_id,
        dm_message_id: target?.dm_message_id,
        reason,
        notes: notes.trim() || undefined,
      });
      toast.success('Report submitted — a moderator will review soon.');
      setReason('spam');
      setNotes('');
      onClose && onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send report';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      data-testid="report-content-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <div className="h-9 w-9 rounded-xl bg-rose-500/15 text-rose-600 flex items-center justify-center flex-shrink-0">
            <Flag className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-rose-600">Report to moderator</div>
            <h3 className="font-heading text-base font-extrabold text-foreground">What&rsquo;s wrong with this?</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              A moderator will review within 48 hours. See{' '}
              <a href="/community-standards" target="_blank" rel="noreferrer" className="underline">Community Standards</a>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
            aria-label="Close"
            data-testid="report-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {target?.snippet && (
          <blockquote className="mt-3 border-l-2 border-primary/40 pl-2 text-xs text-muted-foreground italic line-clamp-3">
            &ldquo;{target.snippet}&rdquo;
          </blockquote>
        )}

        {/* Reason radio */}
        <div className="mt-4 space-y-1.5" role="radiogroup" aria-label="Report reason">
          {REASONS.map((r) => {
            const active = reason === r.value;
            return (
              <label
                key={r.value}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                  active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                }`}
                data-testid={`report-reason-${r.value}`}
              >
                <input
                  type="radio"
                  name="report-reason"
                  value={r.value}
                  checked={active}
                  onChange={(e) => setReason(e.target.value)}
                  className="accent-primary"
                />
                <span className="text-sm font-semibold text-foreground">{r.label}</span>
              </label>
            );
          })}
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground" htmlFor="report-notes">
            Anything moderators should know? (optional)
          </label>
          <textarea
            id="report-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add context — links to other offenses, screenshots described, etc."
            data-testid="report-notes-input"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
          />
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> False reports may affect your standing.
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onClose} disabled={submitting} className="rounded-full font-bold" data-testid="report-cancel-btn">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={submitting}
              className="rounded-full font-bold bg-rose-600 text-white hover:bg-rose-700"
              data-testid="report-submit-btn"
            >
              {submitting ? 'Sending…' : 'Submit report'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
};

export default ReportContentModal;
